<template>
	<div
		class="search-popout"
		ref="searchPopout"
	>
		<focus-lock :returnFocus="true">
			<div class="search-popout__input">
				<label for="search-input" class="sr-only">Search</label>
				<input
					type="text"
					name="search-input"
					id="search-input"
					class="search-input"
					v-model="searchQuery"
				/>
			</div>

			<div
				class="search-popout__body"
				:class="{ 'search-popout__body-no-results' : showWelcome || contents.length === 0 }"
			>

				<loader
					:loading="searching"
					:is-inline="true"
					colour="#ff0000"
				/>

				<template v-if="showWelcome">
					<div class="search-popout__welcome">
						<svg svg-inline="" focusable="false" role="presentation" xmlns="http://www.w3.org/2000/svg" width="43" height="43" viewBox="0 0 43 43" class="search-icon"><g fill="none" fill-rule="evenodd" transform="translate(2 2)"><path fill="#FFF" d="M5.118 17.342v-.003H5c3.66-4.226 7.665-6.338 12.015-6.338 4.349 0 8.269 2.112 11.759 6.338h-.1v.003h.1c-3.49 4.225-7.41 6.338-11.76 6.338-4.349 0-8.354-2.113-12.014-6.338h.118z" class="search-icon__path-1"/><path fill="#581626" d="M13.088 17.16a2.501 2.501 0 1 0 3.073-3.072 4 4 0 1 1-3.072 3.072z" class="search-icon__path-2"/><circle cx="17.038" cy="17.038" r="17.038" stroke="#FFF" stroke-width="3.2" class="search-icon__circle"/><path fill="#FFF" d="M32.044 28.68l7.844 7.846a2.377 2.377 0 1 1-3.362 3.362l-7.844-7.845a2.377 2.377 0 0 1 3.362-3.362z" class="search-icon__path-3"/></g></svg>
						<div>Enter a search term in the box above to get started!</div>
					</div>
				</template>

				<template v-else-if="contents.length > 0">
					<div class="search-popout__number"><span>{{ pagination.total }} results</span> found for <span>'{{ searchQuery }}'</span></div>

					<div class="search-popout__save-all">
						<save-all-content
							:allSaved.sync="allSaved"
							:ids="contentIds"
							:remove="remove"
							:add-save="addSave"
						/>
					</div>

					<div class="search-popout__results">

						<content-listing
							v-for="(content, index) in contents"
							:key="index"
							:content="content"
							:remove="remove"
							:add-save="addSave"
							:show-excerpt="true"
						/>

					</div>

					<pagination
						:pagination.sync="pagination"
						:get-paginated-content="getPaginatedContent"
					/>

				</template>

				<template v-else-if="contents.length === 0">
					<div class="search-popout__welcome">
						Sorry, no results match this term. Please try again.
					</div>
				</template>

			</div>

			<div class="popup-footer search__footer">
				<button
					type="button"
					class="popup-footer__button search-popout__close-button btn--border btn--secondary"
					@click="closeSearch"
				>
					<span>Close Results</span>
					<svg svg-inline="" focusable="false" role="presentation" width="25" height="25" viewBox="0 0 25 25" xmlns="http://www.w3.org/2000/svg"><path d="M15.382 12.583l9.176 9.175-2.85 2.851-9.176-9.175-9.182 9.18-2.862-2.861 9.181-9.181L.494 3.396l2.85-2.85L12.52 9.72 21.701.54l2.863 2.862z" fill="#717171" fill-rule="evenodd"/></svg>
				</button>
			</div>

		</focus-lock>

		<confirmation
			v-if="confirmationOpen"
			heading="Are you sure you want to remove this information?"
			confirm-text="I'm sure"
			:confirm-method="removeSave"
			:confirm-method-params="confirmationRemoveIds"
			close-text="Cancel"
			:close-method="confirmationClose"
		>
			If you remove this information and then change your mind, you'll need to re-add again later.
		</confirmation>
	</div>

</template>

<script>
// Mixins
import { modal } from '@/mixins/modal.js';
import { saves } from '@/mixins/saves.js';

// Components
import FocusLock from 'vue-focus-lock';
import Pagination from '@/components/blocks/Pagination.vue';
import SaveAllContent from '@/components/blocks/SaveAllContent.vue';
import ContentListing from '@/components/blocks/ContentListing.vue';
import Confirmation from '@/components/global/Confirmation.vue';
import Loader from '@/components/utils/Loader.vue';

// Styles
import '@/sass/components/_search.scss';

export default {
	mixins: [modal, saves],
	components: {
		FocusLock,
		Pagination,
		SaveAllContent,
		ContentListing,
		Confirmation,
		Loader
	},
	data() {
		return {
			searchQuery: '',
			searching: false,
			contents: [],
			timeout: null,
			showWelcome: true,
			previousCategory: null,
			pagination: {
				total: 0,
				perPage: 5,
				currentPage: 1,
				totalPages: 0
			}
		};
	},
	watch: {
		searchQuery(val) {
			if (this.timeout) {
				clearTimeout(this.timeout);
			}

			if (val !== '') {
				this.timeout = setTimeout(() => {
					this.pagination.currentPage = 1;
					this.getPaginatedContent();
					this.showWelcome = false;
				}, 500);
			} else {
				this.contents = [];
				this.showWelcome = true;
			}
		},
		$route() {
			this.closeSearch();
		}
	},
	methods: {
		closeSearch() {
			this.$store.commit('searchOpen', false);
			this.removeModalBodyClass();
		},
		getPaginatedContent() {
			this.searching = true;

			// Add filters
			const filters = new Object();
			filters.s = this.searchQuery;
			filters.include = 'topics';

			// Add Pagination to filters
			filters.perPage = this.pagination.perPage;
			filters.page = this.pagination.currentPage;

			if (this.$store.state.selectedRecipients.length > 0) {
				filters.recipientTypes = this.$store.state.selectedRecipients.join();
			}

			this.$content
				.get(filters)
				.then(response => {
					this.$refs.searchPopout.scrollTo(0, 0);
					this.contents = response.collection.all();
					this.pagination.total = response.collection.getTotal();
					this.pagination.totalPages = response.collection.getTotalPages();

					this.$eventBus.$emit('checkAllSaved');

					this.searching = false;
				})
				.catch(() => {
					this.searching = false;
					alert('Sorry, there was an issue, please try again');
				});
		},
		confirmationClose() {
			this.confirmationOpen = false;
		}
	},
	mounted() {
		this.addModalBodyClass();
	},
	beforeDestroy() {
		this.removeModalBodyClass();

		if (this.$route.name === this.$GLOBALS.ROUTE_SHARE_CONTENT) {
			this.$eventBus.$emit('generatePdf');
		}
	}
};
</script>
